import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Input, List, Modal } from 'antd';
import { Tag as TagIcon } from 'lucide-react';
import { TbCrosshair } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useTags, useWatchlists } from '@/queries';
import { tagsService } from '@/services';
import { companySearchResultToSubjectReference } from '@/shared/components/company-search/utils';
import { useWatchlistStore } from '@/stores';
import { SubjectReference } from '@/types';

import { CompanyActionResult } from '..';

type BulkCompanyAddKind = 'tag' | 'watchlist';

interface BulkCompanyAddModalProps {
    kind: BulkCompanyAddKind;
    open: boolean;
    results: CompanyActionResult[];
    onClose: () => void;
}

const BulkCompanyAddModal: FC<BulkCompanyAddModalProps> = ({ kind, open, results, onClose }): JSX.Element => {
    const [search, setSearch] = useState('');
    const [submittingId, setSubmittingId] = useState<string>();
    const queryClient = useQueryClient();
    const addSubjectToWatchlist = useWatchlistStore((state) => state.toggleSubject);
    const tagsQuery = useTags();
    const watchlistsQuery = useWatchlists();
    const isTag = kind === 'tag';
    const loading = isTag ? tagsQuery.isLoading : watchlistsQuery.isLoading;
    const selectedSubjects = useMemo(
        () =>
            results
                .map(companySearchResultToSubjectReference)
                .filter((subject): subject is SubjectReference => Boolean(subject)),
        [results],
    );
    const filteredPrimitives = useMemo(() => {
        const primitives = isTag ? (tagsQuery.data ?? []) : (watchlistsQuery.data ?? []);
        const normalizedSearch = search.trim().toLowerCase();

        return primitives.filter((primitive) => {
            const label = 'name' in primitive ? primitive.name : primitive.title;

            return !normalizedSearch || label.toLowerCase().includes(normalizedSearch);
        });
    }, [isTag, search, tagsQuery.data, watchlistsQuery.data]);

    const addToPrimitive = async (id: string, label: string) => {
        setSubmittingId(id);

        try {
            if (isTag) {
                await Promise.all(selectedSubjects.map((subject) => tagsService.addAssignment(id, subject)));
                await queryClient.invalidateQueries({ queryKey: ['workspace-tags'] });
            } else {
                await Promise.all(selectedSubjects.map((subject) => addSubjectToWatchlist(id, subject, false)));
            }

            toast.success(`Added ${selectedSubjects.length.toLocaleString()} companies to ${label}.`);
            onClose();
        } catch (error) {
            captureException(error);
            toast.error(`Unable to add the selected companies to this ${isTag ? 'tag' : 'watchlist'}.`);
        } finally {
            setSubmittingId(undefined);
        }
    };

    return (
        <Modal
            destroyOnClose
            footer={null}
            open={open}
            title={`Add Selected Companies to ${isTag ? 'Tag' : 'Watchlist'}`}
            width={560}
            onCancel={onClose}
            afterOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    setSearch('');
                    setSubmittingId(undefined);
                }
            }}
        >
            <Input.Search
                allowClear
                placeholder={`Search ${isTag ? 'Tags' : 'Watchlists'}...`}
                style={{ marginBottom: '0.75rem' }}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
            />
            <List
                bordered
                dataSource={filteredPrimitives}
                loading={loading}
                locale={{ emptyText: `No ${isTag ? 'tags' : 'watchlists'} found.` }}
                size='small'
                renderItem={(primitive) => {
                    const label = 'name' in primitive ? primitive.name : primitive.title;

                    return (
                        <List.Item
                            actions={[
                                <Button
                                    disabled={!selectedSubjects.length || Boolean(submittingId)}
                                    key='add'
                                    loading={submittingId === primitive.id}
                                    size='small'
                                    type='primary'
                                    onClick={() => void addToPrimitive(primitive.id, label)}
                                >
                                    Add
                                </Button>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <Avatar
                                        icon={isTag ? <TagIcon size='0.75rem' /> : <TbCrosshair size='0.75rem' />}
                                        shape='square'
                                        size={20}
                                    />
                                }
                                description={<small>{primitive.visibility}</small>}
                                title={label}
                            />
                        </List.Item>
                    );
                }}
            />
        </Modal>
    );
};

export default BulkCompanyAddModal;
